import React, { useState, useEffect, createRef } from 'react';
import { Row, Col, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from 'react-error-boundary';

import PaymentCampaignHolder from '../../styles/paymentCampaignStyle';
import CriticalErrorFallbackComponent from '../../../../shared/components/ErrorFallbacks';

import CardListSummaryComponent from './CardListSummaryComponent';
import CardInfoTotalPriceComponent from './CardInfoTotalPriceComponent';

import PaymentSkeleton from './PaymentSkeleton';
import PaymentActions from './PaymentActions';
import PaymentMethodSelector from './PaymentMethodSelector';
import CashPaymentInfo from './CashPaymentInfo';
import BankTransferInfo from './BankTransferInfo';
import CreditAccountInfo from './CreditAccountInfo';
import CreditCardForm from './CreditCardForm';
import BillingForm from './BillingForm';
import ExternalDspInfo from './ExternalDspInfo';

import usePaymentRedirects from '../../hooks/contentManager/usePaymentRedirects';
import useMercadoPagoSetup from '../../hooks/contentManager/useMercadoPagoSetup';
import usePaymentMethod from '../../hooks/contentManager/usePaymentMethod';
import useBillingForm from '../../hooks/contentManager/useBillingForm';
import useMakePayment from '../../hooks/contentManager/useMakePayment';
import useProposal from '../../hooks/contentManager/useProposal';

import { getDomain } from '../../../../../services/utils';

const PaymentCampaignComponent = props => {
  const {
    user,
    campaignCreated,
    isSuperUser,
    paymentMethods,
    getMercadoPagoPaymentMethods,
    lastBillingInfo,
    fetchingCampaign,
    getCampaign,
    campaign,
    createPayment,
    creatingPayment,
    getProposalRequest,
    getMpInitPointRequest,
    loadingProposal,
    proposalToken,
    currency,
    creatingCampaign,
    paymentData,
    getLastBillingInfoRequest,
    createCampaignComment
  } = props;

  const { t } = useTranslation();
  const domain = getDomain(window.location.hostname);
  const billingInfDniRef = createRef();

  // ── Estado local simple ───────────────────────────────────────────────────
  const [phoneGeneral, setPhoneGeneral] = useState(user && user.phone ? user.phone : '');
  const [matchCampaign, setMatchCampaign] = useState(null);
  const [campaignComment, setCampaignComment] = useState(null);

  // ── Hooks ─────────────────────────────────────────────────────────────────

  // Redirecciones post-pago
  usePaymentRedirects({ paymentData, creatingPayment, creatingCampaign, campaign });

  // Método de pago + tarjeta + bank data
  const {
    paymentMethodSelected,
    selectPaymentMethodSelected,
    creditCardDetails,
    setCreditCardDetails,
    handleSelectPaymentMethod,
    bankData
  } = usePaymentMethod({
    campaign,
    currency,
    paymentMethods,
    isSuperUser,
    user,
    makePayment: () => makePayment()
  });

  // Formulario de facturación
  const { billingInformation, setBillingInformation, handleBillingSource } = useBillingForm({
    lastBillingInfo: lastBillingInfo || {}
  });

  // Lógica de envío
  const { makePayment, creatingToken, errors } = useMakePayment({
    campaign,
    creditCardDetails,
    paymentMethodSelected,
    billingInformation,
    phoneGeneral,
    isSuperUser,
    createPayment,
    getMpInitPointRequest,
    createCampaignComment,
    campaignComment,
    billingInfDniRef
  });

  // MP SDK + identification types + métodos permitidos
  const { identificationTypes, paymentMethodsAllowed } = useMercadoPagoSetup({
    campaign,
    paymentMethods,
    creatingCampaign,
    matchCampaign,
    campaignCreated,
    getCampaign,
    getMercadoPagoPaymentMethods,
    onIdentificationTypesLoaded: docTypeId =>
      setCreditCardDetails(prev => ({ ...prev, documentType: docTypeId }))
  });

  // Propuesta
  const { getProposal } = useProposal({
    loadingProposal,
    proposalToken,
    getProposalRequest,
    campaign
  });

  // ── Efectos residuales ────────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (user) getLastBillingInfoRequest(user.id);
    const campaignParam = params.get('campaignPayment');
    if (campaignParam) setMatchCampaign(campaignParam);
  }, []);

  useEffect(() => {
    if (isSuperUser && campaign && campaign.comments) {
      setCampaignComment(campaign.comments);
    } else {
      setCampaignComment('');
    }
  }, [campaign, isSuperUser]);

  // ── Helpers de render ─────────────────────────────────────────────────────
  const renderPaymentMethods = type => {
    let filtered = paymentMethods
      .filter(pm => pm.type !== 'credit_card' && pm.type === type)
      .filter(
        pm =>
          pm.id === 'transfer' ||
          (pm.min_allowed_amount <= campaign.total_price_with_profit &&
            pm.max_allowed_amount >= campaign.total_price_with_profit)
      );

    if (user && !user.roles.find(r => r === 'agency-member' || r === 'company-member')) {
      filtered = filtered.filter(pm => pm.id !== 'credit_account');
    }

    return filtered.map(pm => (
      <Select.Option key={pm.id} value={pm.id}>
        {pm.id === 'transfer'
          ? t('Transfer')
          : pm.id === 'cash'
          ? t('Cash')
          : pm.id === 'cheque'
          ? t('Check')
          : pm.id === 'credit_account'
          ? t('Current account')
          : pm.name}
      </Select.Option>
    ));
  };

  const handleCreditCardNumberChange = value => {
    setCreditCardDetails(prev => ({ ...prev, number: value }));
    // La lógica de getInstallments se puede mover a useMercadoPagoSetup si se necesita
  };

  // ── Flags de visibilidad ──────────────────────────────────────────────────
  const isExternalDsp = campaign && campaign.external_dsp_id != null && !!campaign.smart_campaign;
  const showPaymentForm = !isExternalDsp || isSuperUser;

  const showCashInfo =
    creditCardDetails &&
    ['cash', 'cheque', 'transfer'].includes(creditCardDetails.paymentMethodId) &&
    paymentMethodSelected === 'other';

  const showBankTransfer =
    campaign &&
    campaign.id &&
    creditCardDetails.paymentMethodId === 'transfer' &&
    paymentMethodSelected === 'other';

  const showCreditAccount =
    creditCardDetails.paymentMethodId === 'credit_account' && paymentMethodSelected !== 'mp';

  // ── Render ────────────────────────────────────────────────────────────────
  if (fetchingCampaign || creatingCampaign) return <PaymentSkeleton />;

  return (
    <ErrorBoundary fallbackRender={({ error }) => <CriticalErrorFallbackComponent error={error} />}>
      <PaymentCampaignHolder>
        <Row>
          <CardListSummaryComponent campaign={campaign} />
          <CardInfoTotalPriceComponent campaign={campaign} />
        </Row>

        <Row type="flex">
          <PaymentActions
            campaign={campaign}
            user={user}
            isSuperUser={isSuperUser}
            loadingProposal={loadingProposal}
            creatingToken={creatingToken}
            onProposal={getProposal}
            onPay={makePayment}
          />

          {showPaymentForm ? (
            <Col xs={24} sm={24} md={24} lg={24} xl={24} className="leftContainer">
              <PaymentMethodSelector
                paymentMethodSelected={paymentMethodSelected}
                creditCardDetails={creditCardDetails}
                paymentMethodsAllowed={paymentMethodsAllowed}
                onSelectGroup={selectPaymentMethodSelected}
                onSelectMethod={handleSelectPaymentMethod}
                renderPaymentMethods={renderPaymentMethods}
              />

              {showCashInfo && (
                <CashPaymentInfo
                  domain={domain}
                  phoneGeneral={phoneGeneral}
                  onPhoneChange={setPhoneGeneral}
                  errors={errors}
                />
              )}

              {showBankTransfer && (
                <BankTransferInfo campaign={campaign} domain={domain} bankData={bankData} />
              )}

              {showCreditAccount && (
                <CreditAccountInfo
                  domain={domain}
                  phoneGeneral={phoneGeneral}
                  onPhoneChange={setPhoneGeneral}
                  errors={errors}
                />
              )}

              {paymentMethodSelected === 'card' && (
                <CreditCardForm
                  creditCardDetails={creditCardDetails}
                  setCreditCardDetails={setCreditCardDetails}
                  identificationTypes={identificationTypes}
                  errors={errors}
                  onCardNumberChange={handleCreditCardNumberChange}
                />
              )}

              <BillingForm
                billingInformation={billingInformation}
                setBillingInformation={setBillingInformation}
                handleBillingSource={handleBillingSource}
                lastBillingInfo={lastBillingInfo || {}}
                campaign={campaign}
                user={user}
                errors={errors}
                billingInfDniRef={billingInfDniRef}
                isSuperUser={isSuperUser}
                campaignComment={campaignComment}
                setCampaignComment={setCampaignComment}
              />
            </Col>
          ) : (
            <ExternalDspInfo campaign={campaign} />
          )}
        </Row>
      </PaymentCampaignHolder>
    </ErrorBoundary>
  );
};

export default PaymentCampaignComponent;
